/**
 * ScamBlocker AI — Sidebar (Glassmorphism)
 * Navigasyon menüsü, rol bazlı görünürlük.
 */

import {
  Activity,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  FileText,
  GraduationCap,
  Hash,
  Key,
  LayoutDashboard,
  Mic,
  Phone,
  Radio,
  Settings,
  UserCog,
  Users,
  Webhook,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { useAuthStore } from '@/shared/stores/authStore';
import { useUIStore } from '@/shared/stores/uiStore';
import styles from './Sidebar.module.css';

interface NavItem {
  to: string;
  icon: React.ReactNode;
  /** i18n anahtarı — Sidebar t() ile çevirir (hardcoded TR metin bırakılmaz, Faz D). */
  label: string;
  permission?: string;
  /** i18n anahtarı (sidebar.section*) */
  section?: string;
  requiresSchoolOrg?: boolean;
  /** Okul kademe 1: org.type==='school' iken menü okul setine daralır —
   *  yalnız schoolVisible işaretli girdiler kalır (Okul/Personel/Raporlar/Ayarlar/Durum). */
  schoolVisible?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', icon: <LayoutDashboard size={20} />, label: 'sidebar.overview', section: 'sidebar.sectionMain' },
  {
    to: '/live',
    icon: <Radio size={20} />,
    label: 'sidebar.liveCalls',
    permission: 'can_live_monitor',
    section: 'sidebar.sectionMain',
  },
  {
    to: '/calls',
    icon: <Phone size={20} />,
    label: 'sidebar.callHistory',
    permission: 'can_live_monitor',
    section: 'sidebar.sectionMain',
  },
  {
    to: '/recordings',
    icon: <Mic size={20} />,
    label: 'sidebar.recordings',
    permission: 'can_listen_recordings',
    section: 'sidebar.sectionMain',
  },
  {
    to: '/staff',
    icon: <UserCog size={20} />,
    label: 'sidebar.staff',
    permission: 'can_manage_members',
    section: 'sidebar.sectionManagement',
    schoolVisible: true,
  },
  {
    to: '/members',
    icon: <Users size={20} />,
    label: 'sidebar.members',
    permission: 'can_manage_members',
    section: 'sidebar.sectionManagement',
  },
  {
    to: '/school',
    icon: <GraduationCap size={20} />,
    label: 'sidebar.school',
    permission: 'can_manage_members',
    section: 'sidebar.sectionManagement',
    requiresSchoolOrg: true,
    schoolVisible: true,
  },
  {
    to: '/api-keys',
    icon: <Key size={20} />,
    label: 'sidebar.apiKeys',
    permission: 'can_manage_keys',
    section: 'sidebar.sectionManagement',
  },
  {
    to: '/branded',
    icon: <Hash size={20} />,
    label: 'sidebar.brandedNumbers',
    permission: 'can_configure',
    section: 'sidebar.sectionManagement',
  },
  {
    to: '/webhooks',
    icon: <Webhook size={20} />,
    label: 'sidebar.webhooks',
    permission: 'can_configure',
    section: 'sidebar.sectionManagement',
  },
  {
    to: '/reports',
    icon: <BarChart3 size={20} />,
    label: 'sidebar.reports',
    permission: 'can_export',
    section: 'sidebar.sectionAudit',
    schoolVisible: true,
  },
  {
    to: '/audit',
    icon: <FileText size={20} />,
    label: 'sidebar.auditTrail',
    permission: 'can_view_audit',
    section: 'sidebar.sectionAudit',
  },
  {
    to: '/settings',
    icon: <Settings size={20} />,
    label: 'sidebar.settings',
    permission: 'can_configure',
    section: 'sidebar.sectionSystem',
    schoolVisible: true,
  },
  { to: '/status', icon: <Activity size={20} />, label: 'sidebar.status', section: 'sidebar.sectionSystem', schoolVisible: true },
];

export function Sidebar() {
  const { t } = useTranslation();
  const { sidebarCollapsed, toggleSidebar, setSidebarMobileOpen } = useUIStore();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const org = useAuthStore((s) => s.org);

  const filteredItems = NAV_ITEMS.filter((item) => {
    if (
      item.permission &&
      !hasPermission(item.permission as keyof import('@/shared/lib/api/types').OrgPermissions)
    ) {
      return false;
    }
    // Fail-closed: org bilgisi eksikse veya org.type 'school' değilse okul girdisi gizlenir.
    if (item.requiresSchoolOrg && org?.type !== 'school') {
      return false;
    }
    // Okul kademe 1: okul org'unda B2B girdileri (canlı/çağrı/kayıt/api-key/
    // markalı/webhook/üyeler/denetim izi) menüden kalkar.
    if (org?.type === 'school' && !item.schoolVisible) {
      return false;
    }
    return true;
  });

  // Bölümlere ayır
  const sections = filteredItems.reduce<Record<string, NavItem[]>>((acc, item) => {
    const section = item.section ?? 'sidebar.sectionOther';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  return (
    <aside className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''} glass`}>
      {/* Logo */}
      <div className={styles.header}>
        <div className={styles.logo}>
          <img src="/logo.jpeg" alt="Ses Guvenlik" className={styles.logoImage} />
          {!sidebarCollapsed && (
            <div className={styles.logoText}>
              <span className={styles.brand}>ScamBlocker</span>
              <span className={styles.orgName}>{org?.name ?? t('sidebar.brandSubtitle')}</span>
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? t('sidebar.expandMenu') : t('sidebar.collapseMenu')}
        >
          {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className={styles.nav}>
        {Object.entries(sections).map(([section, items]) => (
          <div key={section} className={styles.section}>
            {!sidebarCollapsed && <span className={styles.sectionLabel}>{t(section)}</span>}
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
                onClick={() => setSidebarMobileOpen(false)}
                title={sidebarCollapsed ? t(item.label) : undefined}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                {!sidebarCollapsed && <span className={styles.navLabel}>{t(item.label)}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}
